// import { ToastContainer } from 'react-toastify'
// import 'react-toastify/dist/ReactToastify.css'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { GetServerSidePropsContext } from 'next'
import classNames from 'classnames'
import { CraftGlobals } from 'tsconfig/craft-types'
import { useIntercom } from 'react-use-intercom'
import { useMemo, useState } from 'react'
import css from 'styled-jsx/css'
import { getSiteInfo, useSiteInfo } from 'shared'
import getGlobals from '../graphql/get-globals'
import DefaultLayout from '../layouts/default'
import { defaultClient } from '../prismic/clients'
import {
  PrismicImage,
  PrismicMetaSingle,
  PrismicQuestionsSingle,
  PrismicQuizModalSingle,
  PrismicResultsBody,
  PrismicResultsSingle,
  PrismicShareButton,
  PrismicShareButtonsSingle,
} from '../prismic-types-d'
import { CImageSidebar } from '../components/organisms/c-image-sidebar'
import { CQuestions } from '../components/organisms/c-questions'
import { MProgressBar } from '../components/molecules/m-progress-bar'
import { CResultCalculator } from '../components/organisms/c-result-calculator'
import { CResultTop } from '../components/organisms/c-result-top'
import { CResultBottom } from '../components/organisms/c-result-bottom'
import { CSeo } from '../components/organisms/c-seo'

// import { CModalResultsAdvisor } from '../components/organisms/c-modal-results-advisor'
// import { CModalResultsNonAdvisor } from '../components/organisms/c-modal-results-nonadvisor'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const siteInfo = getSiteInfo(context.locale ?? '')

  const global = await getGlobals(siteInfo.handle)

  let questions, results, shareButtons, meta
  let quizModalData = null

  try {
    const questionsRes: PrismicQuestionsSingle = await defaultClient.getSingle(
      'question',
      {
        lang: context.locale?.toLowerCase(),
      }
    )
    questions = questionsRes.data.body

    const resultsRes: PrismicResultsSingle = await defaultClient.getSingle(
      'results',
      {
        lang: context.locale?.toLowerCase(),
      }
    )
    results = resultsRes.data

    if (context.locale === 'en-US') {
      const quizModalRes: PrismicQuizModalSingle =
        await defaultClient.getSingle('quiz_modal', {
          lang: context.locale?.toLowerCase(),
        })
      quizModalData = quizModalRes.data
    }

    const metaRes: PrismicMetaSingle = await defaultClient.getSingle('meta', {
      lang: context.locale?.toLowerCase(),
    })
    meta = metaRes.data

    const shareButtonsRes: PrismicShareButtonsSingle =
      await defaultClient.getSingle('share_button', {
        lang: context.locale?.toLowerCase(),
      })

    shareButtons = shareButtonsRes
      ? shareButtonsRes.data.body.map((item) => {
          return {
            ...item.primary,
          }
        })
      : []
  } catch {
    return {
      notFound: true,
    }
  }

  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=480, stale-while-revalidate=86400'
  )

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      global,
      questions,
      notICLResult: { ...results.body[1], isICL: false },
      iclResult: { ...results.body[0], isICL: true },
      resultSidebar: results.sidebar_image,
      mobileResultSidebar: results.mobile_sidebar_image,
      shareButtons,
      meta,
      quizModalData,
    },
  }
}

type Props = {
  global: CraftGlobals
  questions: PrismicQuestionsSingle['data']['body']
  notICLResult: PrismicResultsBody
  iclResult: PrismicResultsBody
  resultSidebar: PrismicImage
  mobileResultSidebar: PrismicImage
  shareButtons: Array<PrismicShareButton>
  meta: PrismicMetaSingle['data']
  quizModalData: PrismicQuizModalSingle['data']
}

export default function Quiz({
  global,
  questions,
  resultSidebar,
  mobileResultSidebar,
  shareButtons,
  iclResult,
  notICLResult,
  meta,
  quizModalData,
}: Props) {
  const { isUS, isUK } = useSiteInfo()
  const { show: showChat } = useIntercom()
  const [currentQuestion, setCurrentQuestion] = useState<number>(0)
  const [isCalculated, setIsCalculated] = useState<boolean>(false)
  const [answers, setAnswers] = useState<Array<Array<number>>>(
    questions.map((i) => [])
  )

  const quizResult = useMemo(() => {
    if (!questions) {
      return null
    }
    let score = 0
    // loop through answers
    answers.forEach((answer, index) => {
      // Match answer to answer in 'questions' object
      const answerArray = questions[index].items
      let selectedAnswerWeight
      // Check for multi selection questions
      answer.forEach((e) => {
        selectedAnswerWeight = answerArray[e].weight
        score = score + Number(selectedAnswerWeight.split('-')[0])
      })
    })

    return score >= 100 ? notICLResult : iclResult
  }, [answers])

  const formatAnswers = useMemo(() => {
    if (!questions) {
      return null
    }

    const resObj = {
      userResponses: answers.map((answer, index) => {
        const selectedAnswers: Array<string> = []
        answer.forEach((e) => {
          selectedAnswers.push(questions[index].items[e].answer_text)
        })
        return {
          question: questions[index].primary.question_text[0].text,
          selectedAnswer: selectedAnswers,
        }
      }),

      quizStructure: questions.map((question) => {
        return {
          question: question.primary.question_text[0].text,
          answers: question.items.map((e) => e.answer_text),
        }
      }),
    }

    return resObj
  }, [answers])

  const advance = () => {
    if (questions.length - 1 === currentQuestion && (isUS || isUK)) {
      showChat()
    }

    setCurrentQuestion(currentQuestion + 1)
  }

  const previous = () => {
    setAnswers(
      answers.map((item, index) => {
        return answers.length - 1 === currentQuestion ? [] : item
      })
    )

    setCurrentQuestion(currentQuestion - 1)
  }

  const reset = () => {
    setAnswers(questions.map((i) => []))
    setCurrentQuestion(0)
    setIsCalculated(false)
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const showResults = () => {
    setIsCalculated(true)
  }

  const handleAnswerUpdate = (
    current: number,
    index: number,
    checked: boolean
  ) => {
    setAnswers(
      answers.map((item, answerIndex) => {
        if (current === answerIndex) {
          if (questions[current].primary.multiple_choice) {
            if (checked && !answers[current].includes(index)) {
              item.push(index)
              return item
            } else if (!checked && answers[current].includes(index)) {
              const elementIndex = answers[current].findIndex((e) => {
                return e === index
              })
              item.splice(elementIndex, 1)
              return item
            } else {
              return item
            }
          } else {
            return checked ? [index] : []
          }
        } else {
          return item
        }
      })
    )
  }

  return (
    <DefaultLayout global={global}>
      <CSeo meta={meta} />
      <div
        className={classNames('w-full min-h-screen bg-neutral-white', {
          'quiz__question-layout':
            (currentQuestion < questions.length ||
              (currentQuestion >= questions.length && !isCalculated)) &&
            !isUS && !isUK,
          'quiz__result-layout':
            currentQuestion >= questions.length &&
            isCalculated &&
            !isUS && !isUK,
          'quiz__us-layout': (isUS || isUK),
        })}
      >
        {!isUS && !isUK && (
          <div
            className={classNames(
              'quiz__image-sidebar relative flex w-full h-full',
              {
                'quiz__image-sidebar--hide-on-calc':
                  currentQuestion >= questions.length && !isCalculated,
              }
            )}
          >
            <CImageSidebar
              currentQuestion={currentQuestion}
              resultSidebar={resultSidebar}
              mobileResultSidebar={mobileResultSidebar}
              questions={questions}
            />
          </div>
        )}
        {(isUS || isUK) && (
          <MProgressBar
            questions={questions.length}
            currentQuestion={currentQuestion}
          />
        )}
        {currentQuestion < questions.length && (
          <div
            className={classNames(
              'flex items-center px-5 pb-16 md:px-10 md:bg-white quiz__content w-full',
              {
                'flex-col': !isUS && !isUK,
                'flex-col-reverse': (isUS || isUK),
              }
            )}
          >
            <CQuestions
              questions={questions}
              currentQuestion={currentQuestion}
              answers={answers}
              onAdvance={advance}
              onPrevious={previous}
              onUpdate={handleAnswerUpdate}
            />
            {!isUS && !isUK && (
              <MProgressBar
                questions={questions.length}
                currentQuestion={currentQuestion}
              />
            )}
          </div>
        )}
        {currentQuestion >= questions.length && !isCalculated && (
          <CResultCalculator
            className="z-10 bg-neutral-white"
            questions={questions.length}
            currentQuestion={currentQuestion}
            onShowResults={showResults}
          />
        )}
        {currentQuestion >= questions.length && isCalculated && (
          <>
            <CResultTop
              className="z-10 quiz__result-top"
              quizResult={quizResult}
              finalAnswers={formatAnswers}
            />
            {/* Temporary disable */}
            {/* {quizResult?.isICL && ( */}
            {/*   <CModalResultsAdvisor quizModalData={quizModalData} /> */}
            {/* )} */}
            {/* <CModalResultsNonAdvisor quizModalData={quizModalData} /> */}
            {/* <ToastContainer */}
            {/*  position="bottom-center" */}
            {/*  autoClose={2000} */}
            {/*  hideProgressBar */}
            {/*  closeOnClick */}
            {/* /> */}
            {!isUS && !isUK && (
              <CResultBottom
                quizResult={quizResult}
                finalAnswers={formatAnswers}
                shareButtons={shareButtons}
                onReset={reset}
              />
            )}
          </>
        )}
      </div>
      <style jsx>{styles}</style>
    </DefaultLayout>
  )
}

const styles = css`
  .quiz__question-layout {
    display: grid;
    width: 100%;
    grid-template-columns: minmax(200px, 36%) auto;
    grid-template-rows: 1fr;
  }

  .quiz__result-layout {
    display: grid;
    width: 100%;
    grid-template-columns: minmax(200px, 36%) auto;
    grid-template-rows: min-content auto;
  }

  .quiz__us-layout {
    width: 100%;
    padding: 0 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .quiz__content {
    z-index: 10;
  }

  .quiz__question-layout .quiz__image-sidebar {
    grid-column: 1 / 2;
  }

  .quiz__result-layout .quiz__image-sidebar {
    grid-column: 1 / 1;
    grid-row: 1 / 4;
  }

  @media screen and (max-width: 1112px) {
    .quiz__question-layout {
      grid-template-columns: 1fr;
    }

    .quiz__us-layout {
      padding: 0;
    }

    .quiz__image-sidebar--hide-on-calc {
      display: none;
    }

    .quiz__result-layout {
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
    }

    .quiz__result-layout .quiz__image-sidebar {
      grid-column: 1 / 1;
      grid-row: 1 / 1;
    }

    .quiz__content,
    .quiz__image-sidebar {
      grid-area: 1 / 1 / 2 / 2;
    }
  }
`
